import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Grid, Group, Select, Skeleton, Stack, Switch, TextInput } from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { Alert, Button, PageHeader, SectionCard } from "@xplatform/design-system";
import { IconBuildingPlus, IconSearch, IconUserPlus, IconUsers } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import type { AccessUser } from "@models/access";
import { AdminTable, col } from "@components/AdminTable";
import { useAccessScope, useAccessUsers, useOrganisationTree, useRoles } from "@hooks/access";
import { useAdminPermissions } from "@hooks/useAdminPermissions";
import {
  CreateOrganisationModal, CreateUserModal, EnvironmentBanner, OrganisationTree, UserDrawer, organisationName, subtreeSize,
} from "@features/Access";

const PAGE_SIZE = 50;
/** Core lists at most this many organisations at once (422 TOO_MANY_ORGANISATIONS). */
export const MAX_ORGANISATIONS = 20;
const STATUSES = ["all", "ACTIVE", "INACTIVE", "PENDING"] as const;

/**
 * The users of this application in the DSML organisation tree: pick an
 * organisation (and optionally its sub-organisations), search, open a user to
 * change their access. Filters live in the URL, like the Models page.
 */
export const UsersPage = () => {
  const { t } = useTranslation();
  const { canManageAccess } = useAdminPermissions();
  const [params, setParams] = useSearchParams();
  const scope = useAccessScope();
  const tree = useOrganisationTree();
  const roles = useRoles();

  const roots = useMemo(() => tree.data ?? [], [tree.data]);
  const organisationId = params.get("organisationId") ?? scope.data?.rootOrganisationId ?? roots[0]?.id ?? null;
  const includeDescendants = params.get("includeDescendants") === "true";
  const status = (STATUSES as readonly string[]).includes(params.get("status") ?? "") ? params.get("status")! : "all";
  const openUser = params.get("user");
  const [search, setSearch] = useState(params.get("q") ?? "");
  const [q] = useDebouncedValue(search.trim(), 300);
  const [page, setPage] = useState(1);
  const [creating, setCreating] = useState(false);
  const [creatingOrganisation, setCreatingOrganisation] = useState(false);

  const set = (name: string, value: string | null) =>
    setParams((current) => {
      const next = new URLSearchParams(current);
      if (value && value !== "all" && value !== "false") next.set(name, value);
      else next.delete(name);
      return next;
    }, { replace: true });

  // Only the debounced search is written back to the URL.
  const setRef = useRef(set);
  setRef.current = set;
  useEffect(() => setRef.current("q", q || null), [q]);
  useEffect(() => setPage(1), [organisationId, includeDescendants, status, q]);

  const tooMany = includeDescendants && organisationId !== null && subtreeSize(roots, organisationId) > MAX_ORGANISATIONS;
  const filters = organisationId && !tooMany
    ? { organisationId, includeDescendants, q: q || undefined, status: status === "all" ? undefined : status,
        offset: (page - 1) * PAGE_SIZE, limit: PAGE_SIZE }
    : null;
  const users = useAccessUsers(filters);

  const columns = [
    col.twoLine<AccessUser>("name", t("admin.access.users.columns.name"), (u) => ({
      primary: [u.firstName, u.lastName].filter(Boolean).join(" ") || u.email || u.id,
      secondary: u.email ?? "",
    })),
    // A user may belong to several organisations; the column lists those of the scope.
    col.text<AccessUser>("organisation", t("admin.access.users.columns.organisation"),
                         (u) => u.organisations.filter((o) => o.inScope).map((o) => o.name ?? o.id).join(", ") || "-",
                         { width: 200 }),
    col.badge<AccessUser>("role", t("admin.access.users.columns.role"),
                          (u) => u.roles.map((r) => r.name).join(", ") || "-", 160),
    col.status<AccessUser>("status", t("admin.access.users.columns.status"), (u) => u.status ?? "-"),
    col.date<AccessUser>("updated", t("admin.access.users.columns.updated"), (u) => u.updatedAt),
  ];

  const reloadUsers = () => {
    users.reload();
    tree.reload();
  };

  return (
    <Stack gap="md">
      <PageHeader
        title={t("admin.access.users.title")}
        description={t("admin.access.users.description")}
        icon={<IconUsers size={28} />}
        actions={canManageAccess && (
          <Button leftSection={<IconUserPlus size={16} />} onClick={() => setCreating(true)} disabled={!tree.data}>
            {t("admin.access.users.create.open")}
          </Button>
        )}
      />
      <EnvironmentBanner />

      <Grid gutter="md">
        <Grid.Col span={{ base: 12, md: 4, lg: 3 }}>
          <SectionCard
            title={t("admin.access.users.organisations")}
            actions={canManageAccess && (
              <Button size="xs" variant="light" leftSection={<IconBuildingPlus size={14} />}
                      onClick={() => setCreatingOrganisation(true)} disabled={!scope.data || !tree.data}>
                {t("admin.access.organisations.create.open")}
              </Button>
            )}
          >
            {tree.error && <Alert color="red">{tree.error}</Alert>}
            {!tree.data && !tree.error ? <Skeleton h={120} /> : (
              <OrganisationTree roots={roots} selectedId={organisationId} onSelect={(id) => set("organisationId", id)} />
            )}
          </SectionCard>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 8, lg: 9 }}>
          <SectionCard
            title={t("admin.access.users.listTitle", { count: users.data?.total ?? 0 })}
            actions={
              <Group gap="sm" wrap="wrap">
                <TextInput
                  aria-label={t("admin.access.users.search")}
                  placeholder={t("admin.access.users.search")}
                  leftSection={<IconSearch size={16} />}
                  value={search}
                  onChange={(e) => setSearch(e.currentTarget.value)}
                  w={240}
                />
                <Select
                  aria-label={t("admin.access.users.columns.status")}
                  data={STATUSES.map((s) => ({ value: s, label: t(`admin.access.users.statusFilter.${s}`) }))}
                  value={status}
                  onChange={(v) => set("status", v)}
                  allowDeselect={false}
                  w={160}
                  comboboxProps={{ portalProps: { target: "#admin-portal-root" } }}
                />
                <Switch
                  label={t("admin.access.users.includeDescendants")}
                  checked={includeDescendants}
                  onChange={(e) => set("includeDescendants", e.currentTarget.checked ? "true" : null)}
                />
              </Group>
            }
          >
            <Stack gap="sm">
              {tooMany && (
                <Alert color="yellow">{t("admin.access.users.tooMany", { max: MAX_ORGANISATIONS })}</Alert>
              )}
              {users.error && <Alert color={users.forbidden ? "gray" : "red"}>{users.error}</Alert>}
              <AdminTable
                columns={columns}
                data={users.data?.items ?? []}
                getRowKey={(u) => u.id}
                onRowClick={(u) => set("user", u.id)}
                loading={users.loading}
                emptyMessage={t("admin.access.users.empty")}
                pagination={users.data && users.data.total > PAGE_SIZE
                  ? { page, total: users.data.total, pageSize: PAGE_SIZE, onChange: setPage }
                  : undefined}
              />
            </Stack>
          </SectionCard>
        </Grid.Col>
      </Grid>

      <CreateOrganisationModal
        opened={creatingOrganisation}
        rootName={organisationName(roots, scope.data?.rootOrganisationId) ?? ""}
        onClose={() => setCreatingOrganisation(false)}
        onCreated={(organisation) => {
          tree.reload();
          set("organisationId", organisation.id);
        }}
      />
      <CreateUserModal
        opened={creating}
        roots={roots}
        roles={roles.data ?? []}
        defaultOrganisationId={organisationId}
        onClose={() => setCreating(false)}
        onCreated={reloadUsers}
        onFindExisting={(email) => {
          setCreating(false);
          setSearch(email);
        }}
      />
      <UserDrawer
        userId={openUser}
        roots={roots}
        roles={roles.data ?? []}
        canManage={canManageAccess}
        onClose={() => set("user", null)}
        onChanged={reloadUsers}
      />
    </Stack>
  );
};
